import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import Select from 'react-select';
import axios from 'axios';

// DurationInput same as your Add component
const DurationInput = ({ value, onChange }) => {
  const { hours = 0, minutes = 0, seconds = 0 } = value || {};

  const handleChange = (field, val) => {
    let intVal = parseInt(val, 10);
    if (isNaN(intVal) || intVal < 0) intVal = 0;

    if (field === 'hours' && intVal > 23) intVal = 23;
    if ((field === 'minutes' || field === 'seconds') && intVal > 59) intVal = 59;

    const updated = { ...value, [field]: intVal };
    onChange(updated);
  };

  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
      <input
        type="number"
        min="0"
        max="23"
        className="form-control"
        value={hours}
        onChange={e => handleChange('hours', e.target.value)}
        style={{ width: '50px' }}
        aria-label="Hours"
      />
      <span>h</span>
      <input
        type="number"
        min="0"
        max="59"
        className="form-control"
        value={minutes}
        onChange={e => handleChange('minutes', e.target.value)}
        style={{ width: '50px' }}
        aria-label="Minutes"
      />
      <span>m</span>
      <input
        type="number"
        min="0"
        max="59"
        className="form-control"
        value={seconds}
        onChange={e => handleChange('seconds', e.target.value)}
        style={{ width: '50px' }}
        aria-label="Seconds"
      />
      <span>s</span>
    </div>
  );
};

const EditItemStagingProcess = ({ show, onClose, priorityId, onUpdated }) => {
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  const timing_options = [
    { value: '0', label: 'Before Production' },
    { value: '1', label: 'During Production' },
    { value: '2', label: 'After Production' },
  ];

  const [processName, setProcessName] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const [workingDuration, setWorkingDuration] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [workingHours, setWorkingHours] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const formatDuration = ({ hours, minutes, seconds }) =>
    `${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;

  const parseDuration = (str) => {
    // expects format "hh mm ss" like "02h 04m 05s"
    const match = str.match(/(\d{2})h\s(\d{2})m\s(\d{2})s/);
    if (!match) return { hours: 0, minutes: 0, seconds: 0 };
    return {
      hours: parseInt(match[1], 10),
      minutes: parseInt(match[2], 10),
      seconds: parseInt(match[3], 10),
    };
  };

  const isDurationEmpty = ({ hours, minutes, seconds }) =>
    !(hours > 0 || minutes > 0 || seconds > 0);

  // Load existing data when modal opens or priorityId changes
  useEffect(() => {
    if (priorityId && show) {
      axios.get(`${baseURL}/itemstagging/${priorityId}`)
        .then(res => {
          const item = res.data.data;
          setProcessName(item.itemstagging_name || '');
          const timingOpt = timing_options.find(opt => opt.value === String(item.timing)) || null;
          setSelectedOption(timingOpt);
          setWorkingDuration(parseDuration(item.working_duration || '00h 00m 00s'));
          setWorkingHours(parseDuration(item.working_hours || '00h 00m 00s'));
          setErrors({});
          setSubmitted(false);
        })
        .catch(err => {
          console.error('Failed to load staging item:', err);
        });
    }
  }, [priorityId, show]);

  const handleSubmit = async () => {
    setSubmitted(true);
    setErrors({});

    // Basic validations
    if (!processName.trim()) {
      setErrors(prev => ({ ...prev, processName: 'Process Name is required' }));
      return;
    }
    if (!selectedOption) {
      setErrors(prev => ({ ...prev, timing: 'Timing is required' }));
      return;
    }
    if (selectedOption.value !== '1' && isDurationEmpty(workingDuration)) {
      setErrors(prev => ({ ...prev, workingDuration: 'Pre/Post Production Duration is required' }));
      return;
    }
    if (isDurationEmpty(workingHours)) {
      setErrors(prev => ({ ...prev, workingHours: 'Work Duration is required' }));
      return;
    }

    const payload = {
      itemstagging_name: processName.trim(),
      timing: selectedOption.value,
      working_duration: formatDuration(workingDuration),
      working_hours: formatDuration(workingHours),
    };

    setLoading(true);
    try {
      const response = await axios.post(`${baseURL}/itemstagging/${priorityId}`, payload);
      onUpdated && onUpdated();
      onClose();
    } catch (error) {
      const resErrors = error.response?.data?.errors || {};
      setErrors(resErrors);
      alert(error.response?.data?.message || 'Failed to update staging process');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} size="md" onHide={onClose}>
      <div className="modal-content">
        <div className="modal-header" style={{ backgroundColor: '#3ab384' }}>
          <h3 className="modal-title text-white">Update Staging Process</h3>
          <Button variant="" type="button" className="close px-6 py-2" onClick={onClose}>
            <span className="text-white">×</span>
          </Button>
        </div>

        <div className="modal-body">
          <div className="row">
            {/* Process Name */}
            <div className="col-lg-12 mb-2">
              <label className="text-black font-w500 fs-7">
                Process Name<span className="required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter Process Name"
                value={processName}
                onChange={e => setProcessName(e.target.value)}
                disabled={loading}
              />
              {submitted && errors.processName && (
                <span className="text-danger ms-1">{errors.processName}</span>
              )}
            </div>

            {/* Staging Time */}
            <div className="col-lg-12 mb-2">
              <label className="text-black font-w500 fs-7">
                Staging Time<span className="required">*</span>
              </label>
              <Select
                placeholder="Select Staging Time"
                value={selectedOption}
                onChange={setSelectedOption}
                options={timing_options}
            
              />
              {submitted && errors.timing && (
                <span className="text-danger ms-1">{errors.timing}</span>
              )}
            </div>

            {/* Pre/Post Production Duration */}
            {selectedOption?.value !== '1' && (
              <div className="col-lg-12 mb-2">
                <label className="text-black font-w500 fs-7">
                  Pre/Post Production Duration<span className="required">*</span>
                </label>
              
                <DurationInput
                value={workingHours}
                onChange={setWorkingHours}
              />
              {submitted && errors.workingHours && (
                <span className="text-danger ms-1">{errors.workingHours}</span>
              )}
              </div>
            )}

            {/* Work Duration */}
            <div className="col-lg-12 mb-2">
              <label className="text-black font-w500 fs-7">
                Work Duration<span className="required">*</span>
              </label>
                <DurationInput
                  value={workingDuration}
                  onChange={setWorkingDuration}
                />
                {submitted && errors.workingDuration && (
                  <span className="text-danger ms-1">{errors.workingDuration}</span>
                )}
            </div>
          </div>

          <div className="d-flex align-items-center justify-content-end gap-3 mt-4 mb-4">
            <Button
              className="btn btn-sm btn-primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Staging Process'}
              <i className="fas fa-angle-double-right ms-2"></i>
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EditItemStagingProcess;
